import React, { useEffect, useMemo, useRef, useState } from "react";
import { Bell, Check, CheckCheck, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDistanceToNow, parseISO } from "date-fns";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

/**
 * Notification bell + dropdown feed.  Polls every 45s (cheap, no websocket
 * plumbing).  Renders unread count on the bell and lets the user mark
 * individual / all notifications as read.
 */
export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const navigate = useNavigate();
  // Track ids we've already surfaced as browser notifications so we don't
  // re-alert on every poll cycle. Initialised on first load — the initial
  // batch is treated as "already seen" so we don't spam on page refresh.
  const seenIdsRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications?limit=30");
      const arr = Array.isArray(data) ? data : [];
      // Fire an OS/browser notification for every NEW unread item that
      // arrived since the last poll. Requires user to have granted
      // permission on the Settings page.
      if (seenIdsRef.current === null) {
        seenIdsRef.current = new Set(arr.map(n => n.id));
      } else if ("Notification" in window && Notification.permission === "granted") {
        for (const n of arr) {
          if (!n.read && !seenIdsRef.current.has(n.id)) {
            try {
              new Notification(n.title || "Patient Access", {
                body: n.body || "",
                icon: "/icon-192.png",
                badge: "/icon-192.png",
                tag: `pa-${n.id}`,
              });
            } catch { /* ignore */ }
          }
          seenIdsRef.current.add(n.id);
        }
      } else {
        arr.forEach(n => seenIdsRef.current.add(n.id));
      }
      setItems(arr);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 45_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  const unread = useMemo(() => items.filter(n => !n.read).length, [items]);

  const markOne = async (id) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      setItems(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    } catch { /* ignore */ }
  };
  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems(prev => prev.map(n => ({ ...n, read: true })));
      toast.success("Marked everything as read");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    }
  };

  const openItem = (n) => {
    if (!n.read) markOne(n.id);
    if (n.link && navigate) {
      setOpen(false);
      try { navigate(n.link); } catch { window.location.assign(n.link); }
    }
  };

  return (
    <div className="relative" ref={boxRef}>
      <button
        aria-label="Notifications"
        className="relative w-9 h-9 grid place-items-center rounded-full hover:bg-slate-100 text-slate-700"
        onClick={() => setOpen(v => !v)}
        data-testid="notif-bell"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span
            className="absolute top-0.5 right-0.5 min-w-[16px] h-[16px] px-[3px] rounded-full bg-red-600 text-white text-[10px] font-bold grid place-items-center"
            data-testid="notif-badge"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[360px] max-w-[92vw] rounded-xl border border-slate-200 bg-white shadow-2xl z-50 overflow-hidden"
          data-testid="notif-panel"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
            <div className="font-display font-semibold text-sm">Notifications</div>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button
                  onClick={markAll}
                  className="text-[11px] text-slate-500 hover:text-blue-700 inline-flex items-center gap-1"
                  data-testid="notif-mark-all"
                >
                  <CheckCheck size={12} /> Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700 ml-2" aria-label="Close">
                <X size={14} />
              </button>
            </div>
          </div>

          <div className="max-h-[70vh] overflow-y-auto thin-scroll">
            {loading && items.length === 0 && (
              <div className="text-center text-xs text-slate-500 py-6">Loading…</div>
            )}
            {!loading && items.length === 0 && (
              <div className="text-center text-xs text-slate-500 py-8">
                You're all caught up.
              </div>
            )}
            {items.map((n) => (
              <button
                key={n.id}
                onClick={() => openItem(n)}
                className={`w-full text-left px-3 py-2 border-b border-slate-100 hover:bg-slate-50 flex gap-2 ${n.read ? "opacity-70" : ""}`}
                data-testid={`notif-item-${n.id}`}
              >
                <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${n.read ? "bg-slate-300" : "bg-blue-600"}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-800 truncate">{n.title}</div>
                  <div className="text-xs text-slate-500 line-clamp-2">{n.body}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    {(() => { try { return formatDistanceToNow(parseISO(n.created_at), { addSuffix: true }); } catch { return ""; } })()}
                  </div>
                </div>
                {!n.read && (
                  <span
                    onClick={(e) => { e.stopPropagation(); markOne(n.id); }}
                    className="text-slate-300 hover:text-blue-600 shrink-0 self-start p-1"
                    title="Mark read"
                    data-testid={`notif-mark-${n.id}`}
                  >
                    <Check size={12} />
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
